import type { ReactNode } from "react";
import React, { Suspense, useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";

import type { GridSpacing } from "@braintrust/braintrust-ui-components";
import {
  Box,
  Grid,
  IconButton,
  Loader,
  Stack,
  SwipeableDrawer,
} from "@braintrust/braintrust-ui-components";
import { useMediaQuery } from "@braintrust/braintrust-ui-components";
import { MenuIcon } from "@braintrust/braintrust-ui-components/Icons";
import { UserSidebar } from "@js/apps/common/components/user-sidebar";
import { useUser } from "@js/apps/common/hooks/use-user";
import { AirAiTopBar } from "@js/apps/dashboard/components/air-ai-bar";
import { CompleteProfileTopBar } from "@js/apps/dashboard/components/complete-profile-bar";
import { PaymentMethodFailedTopBar } from "@js/apps/employer/components/payment-method-failed-top-bar";
import { NavigationProfileCompletionNudgeCard } from "@js/apps/freelancer/components/profile-completion-nudge";
import { UniversalSearchForm } from "@js/apps/universal-search";
import { Logo } from "@js/components/logo";
import { useAppSelector } from "@js/hooks";
import type { User } from "@js/types/auth";

import type { CoreLayoutProps } from "../core";
import { CoreLayout } from "../core";

import { AppLayoutFooter } from "./components/footer";
import { HelpCenterLink } from "./components/help-center-link";
import { useAlertsPortal } from "./hooks/use-alerts-portal";
import { LeftColumn, Navbar } from "./components";
import { COMMON_Z_INDEX, HEADER_HEIGHT } from "./constants";

import styles from "./styles.module.scss";

export type OverwriteContentComponentProps = {
  alertsElement: ReactNode;
  headerBoxProps: {
    top: number;
    zIndex: number;
  };
  contentBoxProps: {
    className: string;
    overflow: string;
  };
};

export type AppLayoutProps = {
  children: React.ReactNode;
  className?: string;
  coreClassName?: string;
  /**
   *  By default, all immediate children are aligned in row (inline), you can change this
   *  behavior by setting this to true (then all immediate children will be distributed
   *  in new lines).
   */
  flexColumn?: boolean;
  hideHeader?: boolean;
  hideMenu?: boolean;
  showFooter?: boolean;
  spacing?: GridSpacing;
  showMenuToggleAllTheTime?: boolean;
  bgcolor?: string;
  OverwriteContentComponent?: (
    props: OverwriteContentComponentProps,
  ) => ReactNode;
  renderOverwriteNavigationComponent?: (props: TopNavigationProps) => ReactNode;
  disableSubheaderPortal?: boolean;
  /**
   *  Used on pages with virtualization to fix the issue of the white background flickering
   */
  setBgOnBody?: boolean;
} & Pick<CoreLayoutProps, "pageTitle" | "hideMetaTags">;

export const AppLayout: React.FC<React.PropsWithChildren<AppLayoutProps>> = ({
  children,
  className,
  coreClassName,
  flexColumn,
  spacing = 0,
  hideHeader,
  hideMenu,
  showFooter,
  showMenuToggleAllTheTime,
  bgcolor,
  OverwriteContentComponent,
  renderOverwriteNavigationComponent,
  disableSubheaderPortal,
  setBgOnBody,
  ...rest
}) => {
  const showLeftNavGlobal = useAppSelector(
    (state) => state.common.showLeftSidebar,
  );
  const layoutBgColor = useAppSelector((state) => state.common.layoutBgColor);

  const user = useUser();

  const [open, setOpen] = useState(false);

  const handleCloseDrawer = () => setOpen(false);

  const isTablet = useMediaQuery("md");

  const pageBackgroundColor = bgcolor || layoutBgColor;

  const sidebar = user ? <UserSidebar /> : null;

  const showLeftNav =
    !!user &&
    !hideMenu &&
    !isTablet &&
    !showMenuToggleAllTheTime &&
    showLeftNavGlobal;

  useLayoutEffect(() => {
    if (!pageBackgroundColor || !setBgOnBody) {
      return;
    }

    document.body.style.setProperty("--page-bg", pageBackgroundColor);
    return () => {
      document.body.style.removeProperty("--page-bg");
    };
  }, [setBgOnBody, pageBackgroundColor]);

  return (
    <CoreLayout
      {...rest}
      className={classNames(styles.appLayout, coreClassName)}
    >
      <CompleteProfileTopBar />
      <AirAiTopBar />
      <PaymentMethodFailedTopBar />
      {sidebar && (
        <NavDrawer
          leftColumnBgColor={pageBackgroundColor}
          sidebar={sidebar}
          setClose={handleCloseDrawer}
          open={open}
        />
      )}
      <Stack sx={{ flexGrow: 1, bgcolor: pageBackgroundColor }}>
        {!hideHeader && (
          <Header
            bgcolor={bgcolor}
            isTablet={isTablet}
            hideMenu={hideMenu}
            showMenuToggleAllTheTime={showMenuToggleAllTheTime}
            user={user}
            setOpen={setOpen}
            disableSubheaderPortal={disableSubheaderPortal}
            renderOverwriteNavigationComponent={
              renderOverwriteNavigationComponent
            }
          />
        )}
        <Stack direction="row" sx={{ flexGrow: 1 }}>
          {showLeftNav && sidebar && (
            <LeftColumn bgcolor={pageBackgroundColor} sidebar={sidebar} />
          )}
          <Stack sx={{ minWidth: 0, flexGrow: 1, position: "relative" }}>
            <MainContent
              OverwriteContentComponent={OverwriteContentComponent}
              className={className}
              flexColumn={flexColumn}
              spacing={spacing}
            >
              {children}
            </MainContent>
            {!!showFooter && <AppLayoutFooter />}
          </Stack>
        </Stack>
      </Stack>
    </CoreLayout>
  );
};

const AlertsPortalTarget = React.memo(() => {
  const alertsPortalRef = useRef<HTMLDivElement>(undefined);
  const alertsHeight = useAlertsPortal(alertsPortalRef);

  return (
    <Box
      id="alerts-portal"
      ref={alertsPortalRef}
      sx={{
        zIndex: COMMON_Z_INDEX,
        overflow: "hidden",
        position: "sticky",
        height: alertsHeight,
      }}
    />
  );
});

type MainContentProps = {
  children: ReactNode;
  className?: string;
  flexColumn?: boolean;
  spacing?: GridSpacing;
  OverwriteContentComponent?: (
    props: OverwriteContentComponentProps,
  ) => ReactNode;
};

const MainContent = ({
  children,
  className,
  flexColumn,
  spacing,
  OverwriteContentComponent,
}: MainContentProps) => {
  if (OverwriteContentComponent) {
    return (
      <Stack sx={{ flexGrow: 1 }}>
        <Suspense fallback={<Loader centered />}>
          <OverwriteContentComponent
            contentBoxProps={{
              className: classNames(styles.appLayoutContent, className),
              overflow: "hidden",
            }}
            headerBoxProps={{
              top: HEADER_HEIGHT,
              zIndex: COMMON_Z_INDEX - 1,
            }}
            alertsElement={<AlertsPortalTarget />}
          />
        </Suspense>
      </Stack>
    );
  }

  return (
    <Stack sx={{ flexGrow: 1, overflow: "hidden", position: "relative" }}>
      <AlertsPortalTarget />
      <Grid
        container
        spacing={spacing}
        className={classNames(
          styles.appLayoutContent,
          className,
          "overwrite-layout-content",
        )}
        style={{
          flexDirection: flexColumn ? "column" : "row",
          flexWrap: flexColumn ? "nowrap" : undefined,
        }}
      >
        <Suspense fallback={<Loader centered />}>{children}</Suspense>
      </Grid>
    </Stack>
  );
};

type HeaderProps = TopNavigationProps & {
  renderOverwriteNavigationComponent?: (props: TopNavigationProps) => ReactNode;
};

const Header = ({
  renderOverwriteNavigationComponent,
  ...topNavigationProps
}: HeaderProps) => {
  if (renderOverwriteNavigationComponent) {
    return <>{renderOverwriteNavigationComponent(topNavigationProps)}</>;
  }

  return <TopNavigationComponent {...topNavigationProps} />;
};

export type TopNavigationProps = {
  bgcolor?: string;
  isTablet: boolean;
  hideMenu?: boolean;
  showMenuToggleAllTheTime?: boolean;
  user: User | null;
  setOpen: (val: boolean) => void;
  disableSubheaderPortal?: boolean;
};

export const TopNavigationComponent = ({
  bgcolor,
  isTablet,
  hideMenu,
  showMenuToggleAllTheTime,
  user,
  setOpen,
  disableSubheaderPortal,
}: TopNavigationProps) => {
  const shouldShowNavInDawer =
    (isTablet && !hideMenu) || showMenuToggleAllTheTime;
  const isNavInDrawer = shouldShowNavInDawer && user;
  return (
    <NavLayoutContainer bgcolor={bgcolor}>
      <Stack
        direction="row"
        sx={{
          width: 1,
          height: HEADER_HEIGHT,
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", flexGrow: 1, alignItems: "center" }}>
          {isNavInDrawer && (
            <Box>
              <IconButton
                aria-label="open navigation menu"
                onClick={() => setOpen(true)}
                variant="transparent"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
          {!isNavInDrawer && (
            <Stack sx={{ mr: { xs: 2, sm: 3, md: 4 } }}>
              <Logo variant="full-black" />
            </Stack>
          )}
          <UniversalSearchForm />
        </Box>
        <Navbar />
      </Stack>
      {!disableSubheaderPortal && <div id="subheader-portal" />}
    </NavLayoutContainer>
  );
};

type NavDrawerProps = {
  sidebar: ReactNode;
  bgcolor?: string;
  setClose: () => void;
  leftColumnBgColor?: string;
  open: boolean;
};

const NavDrawer = ({ setClose, open, leftColumnBgColor }: NavDrawerProps) => {
  const onOpen = () => null;

  return (
    <SwipeableDrawer
      slotProps={{
        paper: {
          sx: {
            background: leftColumnBgColor,
            maxWidth: 269,
            width: "100%",
          },
        },
      }}
      open={open}
      onOpen={onOpen}
      onClose={setClose}
      onClick={setClose}
      disableSwipeToOpen
      disableDiscovery
      ModalProps={{ keepMounted: false }}
    >
      <Box sx={{ display: "flex", mt: 3.5, ml: 3.25 }}>
        <Logo />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100vh",
          p: 2,
        }}
      >
        <UserSidebar />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <NavigationProfileCompletionNudgeCard />
          <HelpCenterLink />
        </Box>
      </Box>
    </SwipeableDrawer>
  );
};

type NavLayoutContainerProps = {
  children: React.ReactNode;
  bgcolor?: string;
  className?: string;
};

export const NavLayoutContainer = ({
  children,
  bgcolor = "var(--white)",
  ...props
}: NavLayoutContainerProps) => {
  return (
    <Box
      sx={{
        zIndex: COMMON_Z_INDEX + 1,
        position: "sticky",
        top: 0,
        p: {
          xs: "0 5px 0 5px",
          md: "0 22px",
        },
        minWidth: 320,
        display: "flex",
        flexDirection: "column",
        bgcolor,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};
